/**
 * First-run help, ported from Ringo 3D's hints.js and made per-account:
 * a spotlight tour over the live Today screen the first time it appears, and
 * one-time nudges at the moments that confuse new users. Seen keys are
 * cached in localStorage and synced to the account (PATCH /user/hints), so
 * the phone and the desktop agree. Everything is skippable and replayable
 * from "Show me around" in the You sheet.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { updateHints } from './api';

const KEY = 'nav_hints';
const HintsContext = createContext(null);

function readLocal() {
  try { return JSON.parse(localStorage.getItem(KEY) || '{}') || {}; } catch { return {}; }
}
function writeLocal(m) {
  try { localStorage.setItem(KEY, JSON.stringify(m)); } catch { /* private mode */ }
}

export function HintsProvider({ children }) {
  const [seen, setSeen] = useState(readLocal);
  const [tourOpen, setTourOpen] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);

  // Merge what the account remembers (from /state) with this device.
  const hydrate = useCallback((serverSeen) => {
    if (!serverSeen || typeof serverSeen !== 'object') { setHydrated(true); return; }
    setSeen((local) => { const m = { ...serverSeen, ...local }; writeLocal(m); return m; });
    setHydrated(true);
  }, []);

  const markSeen = useCallback((...keys) => {
    if (!keys.length) return;
    setSeen((s) => { const m = { ...s }; keys.forEach((k) => { m[k] = Date.now(); }); writeLocal(m); return m; });
    updateHints({ seen: keys }).catch(() => {});
  }, []);

  const hasSeen = useCallback((k) => !!seen[k], [seen]);

  const reset = useCallback(() => {
    setSeen({});
    try { localStorage.removeItem(KEY); } catch { /* ignore */ }
    updateHints({ reset: true }).catch(() => {});
  }, []);

  const startTour = useCallback(() => setTourOpen(true), []);
  const endTour = useCallback((completed) => { setTourOpen(false); markSeen('tour', completed ? 'tour_done' : 'tour_skipped'); }, [markSeen]);
  const openVideo = useCallback(() => setVideoOpen(true), []);
  const closeVideo = useCallback(() => setVideoOpen(false), []);

  const value = useMemo(() => ({
    seen, hydrated, hasSeen, markSeen, reset, hydrate,
    tourOpen, startTour, endTour, videoOpen, openVideo, closeVideo,
  }), [seen, hydrated, hasSeen, markSeen, reset, hydrate, tourOpen, startTour, endTour, videoOpen, openVideo, closeVideo]);

  return <HintsContext.Provider value={value}>{children}</HintsContext.Provider>;
}

export function useHints() {
  return useContext(HintsContext);
}

/**
 * One-time nudge: returns true the first time `when` is true for this key,
 * until dismissed. Never while the tour is open.
 */
export function useNudge(key, when) {
  const h = useHints();
  const [dismissed, setDismissed] = useState(false);
  const show = !!h && h.hydrated && !h.tourOpen && !h.hasSeen(key) && !dismissed && !!when;
  const dismiss = useCallback(() => { setDismissed(true); h?.markSeen(key); }, [h, key]);
  return { show, dismiss };
}

export const VIDEO_SRC = '/video/how-to-use-the-navigator.mp4';
export const VIDEO_POSTER = '/video/how-to-use-the-navigator.jpg';

/** The Today tour. Targets are ids / data-tour attributes on live elements. */
export const TOUR_STEPS = [
  { target: '#day-arc', title: 'Your day, in five steps', text: 'Plan, Prime, Do, Review, Rest. The dots fill in as the day happens. Tap any of them to jump there.' },
  { target: '#start-planning', title: 'Start here each morning', text: 'Five minutes to pick the three to five things that mean you have won the day. The Navigator remembers what you said and asks about it later.' },
  { target: '#deep-rest-card', title: 'Deep Rest', text: 'After you plan, a six-minute Prime rehearses the day. After the evening review, a twelve-minute Rest closes it. Choose 5, 10 or 15 minutes. Headphones on.' },
  { target: '[data-tour="tab-coach"]', title: 'Coach', text: 'Real coaching conversations, one per life area. Each area can make its own Deep Rest.' },
  { target: '[data-tour="tab-map"]', title: 'Map', text: 'What the Navigator sees across your whole life: patterns, follow-through, the standing read.' },
  { target: '[data-tour="tab-you"]', title: 'You', text: 'Settings, notifications, your voice, and this tour again any time.' },
];
